import { useQuery } from '@tanstack/react-query';
import { Plus, Plug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConnectorCard } from './ConnectorCard';
import { AddConnectorDialog } from './AddConnectorDialog';
import { api } from '@/lib/api';
import { useState, useEffect } from 'react';

interface ConnectorsTabProps {
  readonly openAddDialog: boolean;
  readonly onAddDialogClose: () => void;
}

export function ConnectorsTab({ openAddDialog, onAddDialogClose }: ConnectorsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (openAddDialog) {
      setDialogOpen(true);
      onAddDialogClose();
    }
  }, [openAddDialog, onAddDialogClose]);

  const { data: connectors, isLoading } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => api.admin.connectors.list(),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 pt-6">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {connectors?.length ?? 0} Connector{connectors?.length === 1 ? '' : 's'}
        </h3>
        <Button
          className="bg-teal text-white hover:bg-teal/90"
          size="sm"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Connector
        </Button>
      </div>

      {connectors && connectors.length > 0 ? (
        <div className="space-y-3">
          {connectors.map((c) => (
            <ConnectorCard key={c.configId} config={c} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Plug}
          title="No connectors configured"
          description="Connect your document sources to enable automatic policy publishing."
          action={
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Your First Connector
            </Button>
          }
        />
      )}

      <AddConnectorDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
